import React from "react";
import { styled } from "@mui/material/styles";
import { Box, Paper } from "@mui/material";
import { useSelector } from "react-redux";
import { typeIsGame, useGetDesc } from "../utils/utils";
import { useTranslation } from "react-i18next";
import QuickActionsDetails from "../addComponentDialog/QuickActionsDetails";
import ConfiguratorIcons from "../ConfiguratorIcons";

import MyLocationIcon from "@mui/icons-material/MyLocation";
import NorthIcon from "@mui/icons-material/North";
import EastIcon from "@mui/icons-material/East";
import SouthIcon from "@mui/icons-material/South";
import WestIcon from "@mui/icons-material/West";
import { useViewport } from "../../../helpers";
import clsx from "clsx";
import GameDetails from "./GameDetails";

const displayKeys = ["middle", "north", "east", "south", "west"];

const PREFIX = "defaultDetails";

const classes = {
  root: `${PREFIX}-root`,
  lineDisplay: `${PREFIX}-lineDisplay`,
  lineDisplayBasket: `${PREFIX}-lineDisplayBasket`,
  flexContent: `${PREFIX}-flexContent`,
  layer: `${PREFIX}-layer`,
  previsualisation: `${PREFIX}-previsualisation`,
  type: `${PREFIX}-type`,
  iconsContainer: `${PREFIX}-iconsContainer`,
  wallsIcons: `${PREFIX}-wallsIcons`,
  northIcon: `${PREFIX}-northIcon`,
  eastIcon: `${PREFIX}-eastIcon`,
  southIcon: `${PREFIX}-southIcon`,
  westIcon: `${PREFIX}-westIcon`,
  previewPaintingIcon: `${PREFIX}-previewPaintingIcon`,
};

const DefaultDetailsContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      width: "100%",
    },
    [`& .${classes.lineDisplay}`]: {
      width: "100%",
      minHeight: "50px",
      maxHeight: "90px",
      padding: "0.5rem",
      margin: "3px 0",
      backgroundColor: "#424242",
      borderRadius: "4px",
      display: "flex",
      justifyContent: "space-between",
    },
    [`& .${classes.lineDisplayBasket}`]: {
      width: "100%",
      padding: "0.5rem",
      margin: "3px 0",
      backgroundColor: "#424242",
      overflow: "auto",
      borderRadius: "4px",
      display: "flex",
      justifyContent: "space-between",
      "&::-webkit-scrollbar": {
        width: "5px",
        height: "5px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "rgb(181 154 79 / 58%)",
        borderRadius: "10px",
      },
    },
    [`& .${classes.flexContent}`]: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    [`& .${classes.layer}`]: {
      [theme.breakpoints.between("xs", "sm")]: {},
      marginLeft: "5px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    [`& .${classes.previsualisation}`]: {
      [theme.breakpoints.between("xs", "sm")]: {
        width: "30px",
        marginRight: "0.5rem",
      },
      width: "30px",
      height: "24px",
      marginRight: "1rem",
      minWidth: "20px",
    },
    [`& .${classes.type}`]: {
      [theme.breakpoints.between("xs", "sm")]: {
        width: "inherit",
      },
      maxWidth: "120px",
      textWrap: "noWrap",
    },
    [`& .${classes.iconsContainer}`]: {
      [theme.breakpoints.between("xs", "sm")]: {
        textWrap: "nowrap",
        overflow: "hidden",
      },
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    [`& .${classes.wallsIcons}`]: {
      width: "30px",
      height: "30px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      border: "2px solid rgba(255, 255, 255, .2)",
      webkitBackgroundClip: "padding-box", //For Safari
      backgroundClip: "padding-box", // For IE9+, Firefox 4+, Opera, Chrome
    },
    [`& .${classes.northIcon}`]: {
      borderTop: "3px solid #D3AF5F",
    },
    [`& .${classes.eastIcon}`]: {
      borderRight: "3px solid #D3AF5F",
    },
    [`& .${classes.southIcon}`]: {
      borderBottom: "3px solid #D3AF5F",
    },
    [`& .${classes.westIcon}`]: {
      borderLeft: "3px solid #D3AF5F",
    },
    [`& .${classes.previewPaintingIcon}`]: {
      maxWidth: "70px",
      height: "70px",
    },
  };
});

const iconsToDisplay = {
  middle: <MyLocationIcon fontSize="medium" />,
  north: (
    <Box className={clsx(classes.wallsIcons, classes.northIcon)}>
      <NorthIcon fontSize="medium" />
    </Box>
  ),
  east: (
    <Box className={clsx(classes.wallsIcons, classes.eastIcon)}>
      <EastIcon fontSize="medium" />
    </Box>
  ),
  south: (
    <Box className={clsx(classes.wallsIcons, classes.southIcon)}>
      <SouthIcon fontSize="medium" />
    </Box>
  ),
  west: (
    <Box className={clsx(classes.wallsIcons, classes.westIcon)}>
      <WestIcon fontSize="medium" />
    </Box>
  ),
};

const Description = ({ myTile, descriptionKey }) => {
  const myDesc = useGetDesc(myTile ? myTile[descriptionKey] : null);
  return <div>{myDesc}</div>;
};

const DefaultDetails = () => {
  const { t } = useTranslation();
  const { width } = useViewport();
  const parentLayout = useSelector((state) => state.configurator.parentLayout);
  const selectedTile = useSelector((state) => state.configurator.selectedTile);
  const myTile = useSelector((state) =>
    selectedTile
      ? state.configurator.grid[parentLayout][selectedTile.row][
          selectedTile.column
        ]
      : null
  );

  return (
    <DefaultDetailsContainer sx={{ width: "100%" }}>
      <div className={classes.root}>
        {typeIsGame(myTile?.middle.type) ? (
          <Paper
            key={`preview-on-middle-${myTile.middle.type}-${myTile.row}-${myTile.column}`}
            elevation={7}
          >
            <Box
              className={classes.lineDisplayBasket}
              sx={{ maxHeight: myTile.ground.type ? "200px" : "250px" }}
            >
              <GameDetails />
            </Box>
          </Paper>
        ) : (
          displayKeys.map((key, index) => {
            const isCustomType =
              width >= 768 && myTile[key].type === "custom" && myTile[key].type;
            return (
              <Paper key={`preview-on-${key}-${index + 1}`} elevation={5}>
                <div className={classes.lineDisplay}>
                  <div className={classes.flexContent}>
                    <div className={classes.layer}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          marginRight: width >= 768 ? "20px" : "10px",
                        }}
                      >
                        {Object.values(iconsToDisplay)[index]}
                      </Box>
                      <Box
                        sx={{
                          display: isCustomType ? "block" : "none",
                          marginRight: "20px",
                        }}
                      >
                        {t("myExpariences.form.customModel")}
                      </Box>
                      <Box
                        sx={{
                          display: !isCustomType ? "block" : "none",
                          width: "80%",
                        }}
                      >
                        <Box
                          sx={{
                            display:
                              myTile[key].type !== "frame" &&
                              myTile[key].type !== "custom"
                                ? "flex"
                                : "none",
                            justifyContent: "center",
                          }}
                        >
                          <Box
                            className={classes.previsualisation}
                            sx={{
                              display:
                                myTile[key].type &&
                                myTile[key].type !== "custom"
                                  ? "flex"
                                  : "none",
                            }}
                          >
                            <ConfiguratorIcons
                              orientation={{ type: myTile[key].type }}
                              isForDialog
                              side="neutralPosition"
                            />
                          </Box>
                          <Box
                            className={classes.type}
                            sx={{
                              display:
                                !myTile[key].type || width >= 768
                                  ? "block"
                                  : "none",
                            }}
                          >
                            <Description myTile={myTile} descriptionKey={key} />
                          </Box>
                        </Box>
                      </Box>

                      {/* <div className={myTile[key].type ? null : classes.type}>
                      {myTile[key].type ? null : `${t("myDesign.empty")}`}
                    </div> */}
                      {!myTile[key].iconUrl &&
                        (myTile[key].file || myTile[key].imageUrl) && (
                          <img
                            className={classes.previewPaintingIcon}
                            src={
                              myTile[key].file
                                ? URL.createObjectURL(myTile[key].file)
                                : myTile[key].imageUrl
                            }
                            alt={`preview painting ${key}`}
                          />
                        )}
                      {myTile[key].type === "custom" && myTile[key].iconUrl ? (
                        <img
                          src={myTile[key].iconUrl}
                          alt={`preview-${key}`}
                          style={{
                            maxWidth: "70px",
                            height: "70px",
                            objectFit: "contain",
                            margin: "0 5px",
                          }}
                        />
                      ) : myTile[key].type === "custom" && myTile[key].link ? (
                        <model-viewer
                          src={myTile[key].link}
                          alt="small visio 3D link"
                          shadow-intensity="1"
                          id="model-viewer"
                          min-field-of-view="90deg"
                          max-field-of-view="100deg"
                          camera-orbit="0deg 90deg 0.1m"
                          style={{
                            height: "70px",
                            maxWidth: "70px",
                            transform: "translateY(-2px)",
                          }}
                        >
                          <div
                            id="lazy-load-poster"
                            slot="poster"
                            className={classes.webVrPoster}
                          ></div>
                          <div id="error" className="hide">
                            {t("myDesign.webVR.ARNotSupported")}
                          </div>
                        </model-viewer>
                      ) : null}
                    </div>
                    <div className={classes.iconsContainer}>
                      <QuickActionsDetails layer={key} />
                    </div>
                  </div>
                </div>
              </Paper>
            );
          })
        )}
      </div>
    </DefaultDetailsContainer>
  );
};

export default DefaultDetails;
